import * as Immutable from 'immutable';
import _ from 'lodash';
import {
    EntitySingleTeamSelectInput,
    EntityMultiTeamSelectInput
} from 'common/entity/generic/EntityTeamSelectInput';
import { ReactTelInput } from 'common/components/core/react_tel_input';
import { SelectTypes } from 'common/components/core/select';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import { CitySelector } from 'common/entity/generic/CitySelector';
import { PincodeSelector } from 'common/entity/generic/PincodeSelector';

import EntityWorkingDaysInput from 'common/entity/components/dayShift/EntityWorkingDaysInput';
import EntityWorkingDaysInputWithOffset from 'common/entity/components/dayShift/EntityWorkingDaysInputWithOffset';
import EntityMultiDaySlotsInput from 'common/entity/components/multiDaySlot/EntityMultiDaySlotsInput';
import ZonesInput from 'common/entity/generic/ZonesInput';
import EntityPlacesSearchBox from 'common/entity/generic/EntityPlacesSearchBox';
import EntityContactInput from 'common/entity/contactMaster/EntityContactInput';
import EntityMapWithGeocoding from 'common/entity/generic/EntityMapWithGeocoding';
import EntityDateTimePicker from 'common/entity/generic/EntityDateTimePicker';
import EntityOutletLocationCodeInput from 'common/entity/generic/EntityOutletLocationCodeInput';
import EntityLocationCodeInput from 'common/entity/generic/EntityLocationCodeInput';
import RiderIdInput from 'common/entity/outletMaster/RiderIdInput';
import ShiftIdInput from 'common/entity/vehicleMaster/ShiftIdInput';
import RiderPersonaIdInputWithLimits from 'common/entity/riderMaster/RiderPersonaIdInputWithLimits';
import EntityNotesInput from 'common/entity/generic/EntityNotesInput';
import EntitySkillsInput from 'common/entity/components/skill/EntitySkillsInput';
import CustomValuesInput from 'common/entity/generic/CustomValuesInput';
import EntityLineItemCodeInput from 'common/entity/generic/EntityLineItemCodeInput';
import EntityTransactionDurationInput from 'common/entity/generic/EntityTransactionDurationInput';
import SalesCategoryIdInput from 'common/entity/riderMaster/SalesCategoryIdInput';
import NumberUtils from 'common/utils/NumberUtils';
import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';

const ComponentTypeEnum = {
    FormComponent: 'formComponent',
    Component: 'component'
};

const getEntityComponentForType = ({ type }) => {
    switch (type) {
        case FieldTypeEnum.PlacesSearch:
            return {
                component: EntityPlacesSearchBox,
                defaultProps: ({ field, t }) => {
                    return {
                        location: field.value,
                        popOverContent: field.validationMessage,
                        placeholder: t(field.placeholder)
                    };
                }
            };

        case FieldTypeEnum.WorkingDays:
            return {
                component: EntityWorkingDaysInput,
                defaultProps: ({ field, entity }) => {
                    return {
                        popOverContent: field.validationMessage,
                        defaultWorkingDays: entity.getIn(
                            field.absoluteAccessor
                        ),
                        workingDays: field.value
                    };
                }
            };

        case FieldTypeEnum.WorkingDaysWithOffset:
            return {
                component: EntityWorkingDaysInputWithOffset,
                defaultProps: ({ field, entity }) => {
                    return {
                        popOverContent: field.validationMessage,
                        defaultWorkingDays: entity.getIn(
                            field.absoluteAccessor
                        ),
                        workingDays: field.value
                    };
                }
            };

        case FieldTypeEnum.MultiDaySlots:
            return {
                component: EntityMultiDaySlotsInput,
                defaultProps: ({ field, entity }) => {
                    return {
                        popOverContent: field.validationMessage,
                        defaultSlots: entity.getIn(field.absoluteAccessor),
                        slots: field.value
                    };
                }
            };

        case FieldTypeEnum.Team:
            return {
                component: EntitySingleTeamSelectInput,
                defaultProps: ({ field, props }) => {
                    return {
                        clientId: props.clientId,
                        allTeams: props.teams,
                        teamId: field.value,
                        selectType: SelectTypes.Outline,
                        popOverContent: field.validationMessage,
                        isPopOverOpen: !!field.validationMessage
                    };
                }
            };

        case FieldTypeEnum.Contact:
            return {
                formComponent: EntityContactInput,
                defaultProps: ({ field, props }) => {
                    return {
                        clientId: props.clientId,
                        contact: field.value?.id ? field.value : null
                    };
                }
            };

        case FieldTypeEnum.Skills:
            return {
                formComponent: EntitySkillsInput,
                defaultProps: ({ field, entity, props }) => {
                    return {
                        refreshSkills: () => {
                            return field.fetchInfo.fetcher(
                                field.fetchInfo.params({
                                    field,
                                    entity,
                                    props
                                })
                            );
                        },
                        clientId: props.clientId,
                        skills: field.options({ field }),
                        value: entity.getIn(field.absoluteAccessor)
                    };
                }
            };

        case FieldTypeEnum.MultiChip:
            return {
                formComponent: ZonesInput,
                defaultProps: ({ field, entity, t }) => {
                    return {
                        t,
                        zones: entity.getIn(field.absoluteAccessor)
                    };
                }
            };

        case FieldTypeEnum.Teams:
            return {
                component: EntityMultiTeamSelectInput,
                defaultProps: ({ field, props }) => {
                    return {
                        clientId: props.clientId,
                        allTeams: props.teams,
                        teams: field.value,
                        selectType: SelectTypes.Outline,
                        popOverContent: field.validationMessage,
                        isPopOverOpen: !!field.validationMessage
                    };
                }
            };

        case FieldTypeEnum.Map:
            return {
                formComponent: EntityMapWithGeocoding,
                defaultProps: ({ entity, field, props, t }) => {
                    const { address } = field.extraProps({ entity });
                    return {
                        t,
                        clientId: props.clientId,
                        latLng: field.value,
                        address,
                        defaultCountryCenter: props.defaultCountryCenter
                    };
                }
            };

        case FieldTypeEnum.Phone:
            return {
                component: ReactTelInput,
                defaultProps: ({ field, props }) => {
                    return {
                        value: field.value,
                        defaultCountryISOCode: props?.defaultCountryISOCode,
                        error: field.validationMessage
                    };
                }
            };

        case FieldTypeEnum.DateTimePicker:
            return {
                component: EntityDateTimePicker,
                defaultProps: ({ field, t }) => {
                    return {
                        value: field.value || null,
                        placeholder: t(field.placeholder),
                        error: field.validationMessage
                    };
                }
            };

        case FieldTypeEnum.LocationCodeInput:
            return {
                component: EntityLocationCodeInput,
                defaultProps: ({ entity, field, props, t }) => {
                    return {
                        placeholder: t(field.placeholder),
                        clientId: props.clientId,
                        entity
                    };
                }
            };

        case FieldTypeEnum.OutletLocationCodeInput:
            return {
                component: EntityOutletLocationCodeInput,
                defaultProps: ({ entity, field, props, t }) => {
                    return {
                        placeholder: t(field.placeholder),
                        clientId: props.clientId,
                        entity
                    };
                }
            };

        case FieldTypeEnum.LineItemCodeInput:
            return {
                component: EntityLineItemCodeInput,
                defaultProps: ({ entity, field, props, t }) => {
                    return {
                        placeholder: t(field.placeholder),
                        clientId: props.clientId,
                        entity
                    };
                }
            };

        case FieldTypeEnum.RiderIdInput:
            return {
                component: RiderIdInput,
                defaultProps: ({ field, entity, props }) => {
                    return {
                        rider: field.value?.riderId,
                        clientId: props.clientId,
                        entity
                    };
                }
            };
        case FieldTypeEnum.RiderPersonaIdInputWithLimits:
            return {
                component: RiderPersonaIdInputWithLimits,
                defaultProps: ({ field, entity, props }) => {
                    return {
                        riderPersona: field.value?.riderPersonaId,
                        clientId: props.clientId,
                        popOverContent: field.validationMessage,
                        isPopOverOpen: field.validationMessage,
                        entity
                    };
                }
            };
        case FieldTypeEnum.ShiftIdInput:
            return {
                component: ShiftIdInput,
                defaultProps: ({ entity, props }) => {
                    return {
                        clientId: props.clientId,
                        entity
                    };
                }
            };

        case FieldTypeEnum.TransactionDuration:
            return {
                component: EntityTransactionDurationInput,
                defaultProps: ({ field, t }) => {
                    return {
                        min: '0',
                        rightIcon: t('translations:minutes_short'),
                        defaultValue: NumberUtils.roundOff(
                            (field.value || 0) / 60
                        )
                    };
                }
            };

        case FieldTypeEnum.Notes:
            return {
                component: EntityNotesInput,
                defaultProps: ({ field, entity, t }) => {
                    return {
                        t,
                        placeholder: field.placeholder,
                        notes: entity.getIn(field.absoluteAccessor)
                    };
                }
            };

        case FieldTypeEnum.City:
            return {
                component: CitySelector,
                defaultProps: ({ field, props, t }) => {
                    return {
                        cities: field.options({ field, props }),
                        value: field.value,
                        placeholder: t(field.placeholder),
                        error: field.validationMessage
                    };
                }
            };

        case FieldTypeEnum.Pincode:
            return {
                component: PincodeSelector,
                defaultProps: ({ field, props, t }) => {
                    return {
                        pincodes: field.options({ field, props }),
                        value: field.value,
                        placeholder: t(field.placeholder),
                        error: field.validationMessage
                    };
                }
            };

        case FieldTypeEnum.CustomFields:
            return {
                formComponent: CustomValuesInput,
                defaultProps: ({ entity, field, props }) => {
                    const {
                        settingsName,
                        skipResources = false
                    } = field.extraProps();
                    //filtering out the custom resource limits for VM and RP entities
                    const customFields = CustomColumnHelpers.getCustomFields(
                        props.entitySettings,
                        settingsName,
                        skipResources
                    );
                    return {
                        customFields,
                        returnValuesAsMap: true,
                        values:
                            entity.getIn(field.absoluteAccessor) ||
                            Immutable.Map()
                    };
                }
            };

        case FieldTypeEnum.SalesCategoryId:
            return {
                component: SalesCategoryIdInput,
                defaultProps: ({ entity, props }) => {
                    return {
                        clientId: props.clientId,
                        entity
                    };
                }
            };
    }
};

class EntityAdapter {
    static getFieldsWithComponent({ fields }) {
        return fields.reduce((map, field) => {
            map[field.accessor] = EntityAdapter.getSingleFieldWithComponent({
                field
            });
            return map;
        }, {});
    }

    static filterOutHiddenFields = (fields, props) => {
        return fields.filter(field => {
            if (_.isFunction(field.isHidden)) {
                return !field.isHidden(props);
            }
            return true;
        });
    };

    static getSingleFieldWithComponent({ field }) {
        const modifiedField = { ...field };
        const entityFieldComponent = getEntityComponentForType({
            type: modifiedField.type
        });

        if (entityFieldComponent) {
            const key = entityFieldComponent.component
                ? ComponentTypeEnum.Component
                : ComponentTypeEnum.FormComponent;
            modifiedField[key] = entityFieldComponent[key];
            modifiedField.defaultProps = entityFieldComponent.defaultProps;
        }
        return modifiedField;
    }
}

export default EntityAdapter;
